<template>
  <div class="layout">
    <Sidebar :is-collapse="isCollapse" />
    <div class="main-container">
      <div class="header">
        <el-icon class="toggle-sidebar" @click="toggleCollapse">
          <Fold v-if="!isCollapse" />
          <Expand v-else />
        </el-icon>
        <div class="breadcrumb">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item v-for="(item, index) in breadcrumbList" 
              :key="index"
              :to="item.path"
            >
              {{ item.title }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="user-dropdown">
              <el-avatar :size="32" :icon="UserFilled" />
              <span class="welcome-text">欢迎您，{{ userInfo?.username }}</span>
              <el-icon class="el-icon--right"><CaretBottom /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <!-- <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="password">修改密码</el-dropdown-item> -->
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      
      <el-tabs
        v-model="activeTab"
        type="card"
        class="layout-tabs"
        @tab-click="handleTabClick"
        @tab-remove="removeTab"
      >
        <el-tab-pane
          v-for="item in visitedViews"
          :key="item.path"
          :label="item.title"
          :name="item.path"
          :closable="item.path !== '/'"
        >
        </el-tab-pane>
      </el-tabs>
      
      <div class="content">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Fold, Expand, UserFilled, CaretBottom } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import Sidebar from './components/Sidebar.vue'
import { userApi } from '@/api/user'
import type { User } from '@/types/user'

const route = useRoute()
const router = useRouter()
const isCollapse = ref(false)
const userInfo = ref<User | null>(null)

// 获取用户信息
onMounted(() => {
  const storedUserInfo = localStorage.getItem('userInfo')
  if (storedUserInfo) {
    try {
      userInfo.value = JSON.parse(storedUserInfo)
      console.log('用户信息加载成功:', userInfo.value)
    } catch (error) {
      console.error('解析用户信息失败:', error)
      // 尝试重新获取用户信息
      router.push('/auth/login')
    }
  } else {
    console.warn('未找到用户信息，可能需要重新登录')
  }
})

// 处理下拉菜单命令
const handleCommand = async (command: string) => {
  switch (command) {
    case 'profile':
      router.push('/profile')
      break
    case 'password':
      router.push('/password')
      break
    case 'logout':
      await handleLogout()
      break
  }
}

// 处理登出
const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要退出登录吗？',
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    // 先发送登出请求，此时 token 还在
    // await userApi.logout().catch(error => {
    //   console.error('Logout API error:', error)
    // })

    // 保存现有的用户名和密码
    const savedUsername = localStorage.getItem('username')
    const savedPassword = localStorage.getItem('password')
    
    // 清除登录状态相关数据
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    
    // 如果有保存的用户名和密码，重新保存回去
    if (savedUsername && savedPassword) {
      localStorage.setItem('username', savedUsername)
      localStorage.setItem('password', savedPassword)
    }
    
    ElMessage.success('已退出登录')
    router.push('/auth/login')
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Logout error:', error)
      ElMessage.error('退出登录失败')
    }
  }
}

// 标签页相关
const activeTab = ref('/')
const visitedViews = ref([
  { path: '/', title: '首页' }
])

// 监听路由变化，添加标签页
watch(
  () => route.path,
  (path) => {
    const title = route.meta?.title as string || '未命名'
    if (!visitedViews.value.some(v => v.path === path)) {
      visitedViews.value.push({
        path,
        title
      })
    }
    activeTab.value = path
  },
  { immediate: true }
)

const handleTabClick = (tab: any) => {
  router.push(tab.props.name)
}

const removeTab = (targetPath: string) => {
  const tabs = visitedViews.value
  let activePath = activeTab.value
  
  if (activePath === targetPath) {
    tabs.forEach((tab, index) => {
      if (tab.path === targetPath) {
        const nextTab = tabs[index + 1] || tabs[index - 1]
        if (nextTab) {
          activePath = nextTab.path
        }
      }
    })
  }
  
  visitedViews.value = tabs.filter(tab => tab.path !== targetPath)
  activeTab.value = activePath
  router.push(activePath)
}

const breadcrumbList = computed(() => {
  const matched = route.matched.slice(1)
  return matched.map(item => ({
    path: item.path,
    title: item.meta?.title || '未命名'
  }))
})

const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value
}
</script>

<style lang="scss" scoped>
.layout {
  display: flex;
  height: 100vh;
  width: 100vw;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  
  .main-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    
    .header {
      height: 56px;
      padding: 0 16px;
      background: #fff;
      border-bottom: 1px solid #e8e8e8;
      display: flex;
      align-items: center;
      
      .toggle-sidebar {
        font-size: 20px;
        cursor: pointer;
        margin-right: 16px;
        
        &:hover {
          color: #409EFF;
        }
      }
      
      .breadcrumb {
        flex: 1;
      }
      
      .user-info {
        margin-left: auto;
        padding-left: 16px;
        
        .user-dropdown {
          display: flex;
          align-items: center;
          cursor: pointer;
          
          .welcome-text {
            margin: 0 8px;
            color: #606266;
          }
        }
      }
    }
    
    .layout-tabs {
      background: #fff;
      padding: 6px 16px 0;
      border-bottom: 1px solid #e8e8e8;
      
      :deep(.el-tabs__header) {
        margin: 0;
      }
      
      :deep(.el-tabs__nav) {
        border: none;
      }
      
      :deep(.el-tabs__item) {
        height: 32px;
        line-height: 32px;
        border: 1px solid #e8e8e8;
        border-bottom: none;
        background-color: #f0f2f5;
        
        &.is-active {
          background-color: #fff;
        }
      }
      
      :deep(.el-tabs__content) {
        display: none;
      }
    }
    
    .content {
      flex: 1;
      padding: 0;
      overflow: auto;
      background: #f6f6f6;
      
      :deep(.el-card) {
        margin-bottom: 16px;
      }
    }
  }
}

:global(body) {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

:global(#app) {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}
.el-avatar {
  background-color: #F9620B;
}
</style>